import { useCallback, useRef } from 'react';
import {
    useForm,
    type FieldErrors,
    type FieldValues,
    type Resolver,
    type UseFormHandleSubmit,
    type UseFormProps,
    type UseFormReset,
    type UseFormReturn,
} from 'react-hook-form';

type BlurValidationFormProps<TFieldValues extends FieldValues, TContext, TTransformedValues> = UseFormProps<
    TFieldValues,
    TContext,
    TTransformedValues
> & {
    resolver: Resolver<TFieldValues, TContext, TTransformedValues>;
};

function toPathSegments(path: string): string[] {
    return path
        .replace(/\[(\w+)\]/g, '.$1')
        .split('.')
        .filter(Boolean);
}

function getValueAtPath(value: unknown, path: string): unknown {
    return toPathSegments(path).reduce<unknown>((current, segment) => {
        if (current === null || typeof current !== 'object') {
            return undefined;
        }
        return (current as Record<string, unknown>)[segment];
    }, value);
}

function classifyTextEntryElement(element: unknown): boolean | undefined {
    if (element === null || typeof element !== 'object') {
        return undefined;
    }

    const { tagName, type } = element as { tagName?: string; type?: string };
    if (tagName === 'TEXTAREA') {
        return true;
    }
    if (tagName === 'SELECT') {
        return false;
    }

    if (tagName === 'INPUT') {
        return [
            'date',
            'datetime-local',
            'email',
            'month',
            'number',
            'password',
            'search',
            'tel',
            'text',
            'time',
            'url',
            'week',
        ].includes(type ?? 'text');
    }

    return undefined;
}

function isTextEntryField(field: { ref?: unknown } | undefined, name: string): boolean {
    const refClassification = classifyTextEntryElement(field?.ref);
    if (refClassification !== undefined) {
        return refClassification;
    }

    if (typeof document !== 'undefined') {
        const elementClassification = classifyTextEntryElement(document.getElementsByName(name)[0]);
        if (elementClassification !== undefined) {
            return elementClassification;
        }
    }

    // Controller uses a virtual ref for composed inputs, without DOM tag metadata.
    return true;
}

function isEmptyContainer(value: unknown): boolean {
    if (Array.isArray(value)) {
        return Object.keys(value).length === 0;
    }
    return value !== null && typeof value === 'object' && Object.keys(value).length === 0;
}

function removeErrorAtPath<TFieldValues extends FieldValues>(
    errors: FieldErrors<TFieldValues>,
    path: string
): FieldErrors<TFieldValues> {
    const segments = toPathSegments(path);

    function removeAtPath(current: unknown, index: number): unknown {
        if (current === null || typeof current !== 'object') {
            return current;
        }

        const key = segments[index];
        const currentRecord = current as Record<string, unknown>;
        if (!(key in currentRecord)) {
            return current;
        }

        const clone = Array.isArray(current) ? [...current] : { ...currentRecord };
        const cloneRecord = clone as Record<string, unknown>;

        if (index === segments.length - 1) {
            delete cloneRecord[key];
            return clone;
        }

        const child = removeAtPath(currentRecord[key], index + 1);
        if (child === currentRecord[key]) {
            return current;
        }

        if (isEmptyContainer(child)) {
            delete cloneRecord[key];
        } else {
            cloneRecord[key] = child;
        }
        return clone;
    }

    if (segments.length === 0) {
        return errors;
    }

    return removeAtPath(errors, 0) as FieldErrors<TFieldValues>;
}

/**
 * Keeps blur validation for populated fields while deferring empty-field errors
 * until the shopper attempts to submit the form.
 */
export function useFormWithBlurValidation<
    TFieldValues extends FieldValues = FieldValues,
    TContext = unknown,
    TTransformedValues = TFieldValues,
>(
    props: BlurValidationFormProps<TFieldValues, TContext, TTransformedValues>
): UseFormReturn<TFieldValues, TContext, TTransformedValues> {
    const submitAttemptedRef = useRef(false);
    const { resolver, ...formProps } = props;

    const guardedResolver = useCallback<Resolver<TFieldValues, TContext, TTransformedValues>>(
        async (values, context, options) => {
            const result = await resolver(values, context, options);
            if (submitAttemptedRef.current || !options.names?.length) {
                return result;
            }

            if (Object.keys(result.errors).length === 0) {
                return result;
            }

            let errors = result.errors as FieldErrors<TFieldValues>;
            for (const name of options.names) {
                if (isTextEntryField(options.fields[name], name) && getValueAtPath(values, name) === '') {
                    errors = removeErrorAtPath<TFieldValues>(errors, name);
                }
            }

            if (errors === result.errors) {
                return result;
            }

            if (Object.keys(errors).length === 0) {
                return {
                    values: values as unknown as TTransformedValues,
                    errors: {},
                };
            }

            return {
                values: {},
                errors,
            };
        },
        [resolver]
    );

    const form = useForm<TFieldValues, TContext, TTransformedValues>({
        ...formProps,
        mode: formProps.mode ?? 'onBlur',
        resolver: guardedResolver,
    });
    const baseHandleSubmit = form.handleSubmit;
    const baseReset = form.reset;

    const handleSubmit = useCallback<UseFormHandleSubmit<TFieldValues, TTransformedValues>>(
        (onValid, onInvalid) => {
            const submit = baseHandleSubmit(onValid, onInvalid);
            return async (event) => {
                submitAttemptedRef.current = true;
                await submit(event);
            };
        },
        [baseHandleSubmit]
    );

    const reset = useCallback<UseFormReset<TFieldValues>>(
        (values, keepStateOptions) => {
            if (!keepStateOptions?.keepIsSubmitted && !keepStateOptions?.keepSubmitCount) {
                submitAttemptedRef.current = false;
            }
            baseReset(values, keepStateOptions);
        },
        [baseReset]
    );

    return {
        ...form,
        handleSubmit,
        reset,
    };
}
